import { AppFilterButton, AppFilterButtonType } from '@/app/shared/_components/app-filters/models';
import { ActivityStatus, AppFilter, AppTable, AppTableColumn, PortalType, SortDirection } from '@/app/shared/_models/models';
import { ParsedRequestParams, SFields } from '@libs/crud-request-nestjs';
import { capitalize } from 'lodash';

import { UniversalMediaList } from '../models';

// ################################################################################
// ################################# Types ########################################
// ################################################################################

export type FilterHandler = (filter: AppFilter) => SFields;

// ################################################################################
// ################################# Enums ########################################
// ################################################################################

export enum UniversalMediaListSegmentType {
  MEDIA_DOMAIN = 'mediadomain',
  MEDIA_ID = 'mediaid',
  PUB_ID = 'pubid'
}

export enum UniversalMediaListSegmentTypeDisplayName {
  MEDIA_DOMAIN = 'App Bundles or Site Domains',
  MEDIA_ID = 'App IDs or Site IDs',
  PUB_ID = 'Publisher IDs'
}

export enum UniversalMediaListType {
  SHARED = 'shared',
  CURATED_MARKETPLACE = 'curated_marketplace',
  INTERNAL = 'internal'
}

export enum UniversalMediaListTypeDisplayName {
  SHARED = 'Shared',
  CURATED_MARKETPLACE = 'Curated Marketplace',
  INTERNAL = 'Internal'
}

export const enum UniversalMediaListFilterId {
  MEDIA_LIST_STATUS_FILTER = 'mediaListStatusFilter',
  MEDIA_LIST_SEARCH_FILTER = 'mediaListSearchFilter',
  MEDIA_TYPE_FILTER = 'mediaTypeFilter',
  LIST_TYPE_FILTER = 'listTypeFilter',
  ACCOUNT_FILTER = 'accountFilter',
  ASSOCIATED_DEALS_FILTER = 'associatedDealsFilter',
}

export const enum UniversalMediaListButtonId {
  CREATE_UNIVERSAL_MEDIA_LIST_BUTTON = 'createUniversalMediaListButton',
}

// ################################################################################
// ################################# Options ######################################
// ################################################################################

const STATUS_OPTIONS = [
  { id: 1, displayName: capitalize(ActivityStatus.ACTIVE), value: ActivityStatus.ACTIVE, isDisabled: false },
  { id: 2, displayName: 'Archived', value: ActivityStatus.INACTIVE, isDisabled: false }
];

const MEDIA_TYPE_SHARED_OPTIONS = [
  { id: 1, displayName: UniversalMediaListSegmentTypeDisplayName.MEDIA_DOMAIN, value: UniversalMediaListSegmentType.MEDIA_DOMAIN, isDisabled: false },
  { id: 2, displayName: UniversalMediaListSegmentTypeDisplayName.MEDIA_ID, value: UniversalMediaListSegmentType.MEDIA_ID, isDisabled: false },
  { id: 3, displayName: UniversalMediaListSegmentTypeDisplayName.PUB_ID, value: UniversalMediaListSegmentType.PUB_ID, isDisabled: false }
];

const LIST_TYPE_SHARED_OPTIONS = [
  { id: 2, displayName: UniversalMediaListTypeDisplayName.SHARED, value: UniversalMediaListType.SHARED, isDisabled: false },
  { id: 3, displayName: UniversalMediaListTypeDisplayName.CURATED_MARKETPLACE, value: UniversalMediaListType.CURATED_MARKETPLACE, isDisabled: false }
].sort((a, b) => a.displayName.localeCompare(b.displayName));

const LIST_TYPE_INTERNAL_OPTIONS = [
  ...LIST_TYPE_SHARED_OPTIONS,
  { id: 1, displayName: UniversalMediaListTypeDisplayName.INTERNAL, value: UniversalMediaListType.INTERNAL, isDisabled: false },
].sort((a, b) => a.displayName.localeCompare(b.displayName));

// ################################################################################
// ################################# App Filters ##################################
// ################################################################################

export const getUniversalMediaListsDefaultFilters = (): Partial<ParsedRequestParams> => ({
  search: {
    'segment.status': { $in: ['ACTIVE'] }
  },
  page: 1,
  limit: 10,
  sort: [{
    field: 'createdAt',
    order: SortDirection.DESC
  }],
  join: [{ field: 'segment.dealSegmentGroupSegments.segmentGroup.deal' }, { field: 'segment.segmentMaps' }]
});

export const getDefaultAppFilters = (portalType: PortalType = 'partner'): AppFilter[] => {
  const mediaListStatusFilter = new AppFilter(
    UniversalMediaListFilterId.MEDIA_LIST_STATUS_FILTER,
    'SELECT',
    'status',
    'Status',
    'Filter by status',
    'multiple',
    true,
    false,
    null,
    STATUS_OPTIONS,
    STATUS_OPTIONS.filter(option => option.value === 'ACTIVE'),
  );

  const mediaListSearchFilter = new AppFilter(
    UniversalMediaListFilterId.MEDIA_LIST_SEARCH_FILTER,
    'SEARCH',
    'mediaListName',
    'Media List ID/Name',
    'Search by ID or name',
    'multiple',
    true,
    true,
    { nameProp: 'mediaListName', idProp: 'mediaListId' }
  );

  const mediaTypeFilter = new AppFilter(
    UniversalMediaListFilterId.MEDIA_TYPE_FILTER,
    'SELECT',
    'mediaType',
    'Media Type',
    'Filter by media type',
    'multiple',
    true,
    false,
    null,
    MEDIA_TYPE_SHARED_OPTIONS
  );

  const listTypeFilter = new AppFilter(
    UniversalMediaListFilterId.LIST_TYPE_FILTER,
    'SELECT',
    'listType',
    'List Type',
    'Filter by list type',
    'multiple',
    true,
    false,
    null,
    portalType === 'exchange' ? LIST_TYPE_INTERNAL_OPTIONS : LIST_TYPE_SHARED_OPTIONS
  );

  const accountFilter = new AppFilter(
    UniversalMediaListFilterId.ACCOUNT_FILTER,
    'SELECT',
    'accountId',
    'Account',
    'Filter by account',
    'multiple',
    false,
    null,
    { nameProp: 'name', idProp: 'id' },
    [],
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    true
  );

  const associatedDealsFilter = new AppFilter(
    UniversalMediaListFilterId.ASSOCIATED_DEALS_FILTER,
    'SELECT',
    'dealId',
    'Associated Deals',
    'Filter by associated deals',
    'multiple',
    true,
    true,
    { nameProp: 'dealName', idProp: 'dealId' },
    []
  );

  const appFilters: AppFilter[] = [
    mediaListStatusFilter,
    mediaListSearchFilter,
    mediaTypeFilter,
    listTypeFilter,
    associatedDealsFilter,
  ];

  if (portalType === 'exchange') {
    const listTypeFilterIndex = appFilters.findIndex(filter => filter.id === UniversalMediaListFilterId.LIST_TYPE_FILTER) + 1;
    appFilters.splice(listTypeFilterIndex, 0, accountFilter);
  }

  return appFilters;
};

export const getUniversalMediaListsButtons = (): AppFilterButton[] => [{
  id: UniversalMediaListButtonId.CREATE_UNIVERSAL_MEDIA_LIST_BUTTON,
  type: AppFilterButtonType.PRIMARY_BUTTON,
  title: 'Create Media List',
  hasLoadingSpinner: false, auth: 'canCreate'
}];

// ################################################################################
// ################################# App Table ####################################
// ################################################################################

export const getUniversalMediaListsTable = (): AppTable => new AppTable('universalMediaListsTable', 1, 10, 'createdAt', 'DESC');

export const getUniversalMediaListsTableColumns = (portalType: PortalType): AppTableColumn[] => {
  const statusColumn: AppTableColumn = {
    id: 'status', prop: 'status', displayName: 'Status', type: 'tags-rx-dashboard', isSortEnabled: false,
    colWidth: '100px',
    tags: [
      { id: 'activeTag', name: 'Active', condition: 'active', tooltip: 'Active' },
      { id: 'archivedTag', name: 'Archived', condition: 'inactive', tooltip: 'Archived' },
    ],
  };

  const idColumn: AppTableColumn = {
    id: 'id',
    prop: 'id',
    displayName: 'ID',
    type: 'text',
    isSortEnabled: true,
    colWidth: '50px'
  };

  const nameColumn: AppTableColumn = {
    id: 'name',
    prop: 'name',
    displayName: 'Name',
    type: 'text',
    isSortEnabled: true
  };

  const mediaTypeColumn: AppTableColumn = {
    id: 'mediaType',
    prop: 'mediaType',
    displayName: 'Media Type',
    type: 'custom-function',
    isSortEnabled: true,
    valueFormatter: (value: string) => {
      const mediaTypeKey = Object.keys(UniversalMediaListSegmentType).find(
        key => UniversalMediaListSegmentType[key] === value
      ) as keyof typeof UniversalMediaListSegmentTypeDisplayName;

      return UniversalMediaListSegmentTypeDisplayName[mediaTypeKey];
    }
  };

  const listTypeColumn: AppTableColumn = {
    id: 'listType',
    prop: 'listType',
    displayName: 'List Type',
    type: 'custom-function',
    isSortEnabled: true,
    valueFormatter: (value: string) => {
      const listTypeKey = Object.keys(UniversalMediaListType).find(
        key => UniversalMediaListType[key] === value
      ) as keyof typeof UniversalMediaListTypeDisplayName;

      return UniversalMediaListTypeDisplayName[listTypeKey];
    }
  };

  const createdAtColumn: AppTableColumn = {
    id: 'createdAt',
    prop: 'createdAt',
    displayName: 'Created At (EST)',
    type: 'date',
    isSortEnabled: true
  };

  const updatedAtColumn: AppTableColumn = {
    id: 'updatedAt',
    prop: 'updatedAt',
    displayName: 'Updated At (EST)',
    type: 'date',
    isSortEnabled: true
  };

  const associatedDealsColumn: AppTableColumn = {
    id: 'associatedDeals',
    prop: 'associatedDeals',
    displayName: 'Associated Deals',
    type: 'moreTag',
    isSortEnabled: false,
    moreTag: { 
      prop: 'dealId', 
      displayNameProp: 'dealDisplayName',
      popoverTitle: 'More deals', 
      linked: true 
    },
    valueFormatter: (dealId: number): string[] => {
      if (portalType !== 'exchange') {
        return [`/${portalType}/curated-marketplace-deals`, dealId.toString(), 'edit'];
      } else {
        return [`/${portalType}/exchange-deals`, dealId.toString(), 'edit'];
      }
    },
  };

  const mediaCountColumn: AppTableColumn = {
    id: 'mediaCount',
    prop: 'mediaCount',
    displayName: 'Media Count',
    type: 'number',
    isSortEnabled: false,
  };

  const hasUniversalMediaListAccess = (row: UniversalMediaList, status?: ActivityStatus): boolean => {
    if (status && row.status !== status) {
      return false;
    }

    if (portalType === 'exchange') {
      return true;
    }

    return row.listType === UniversalMediaListType.CURATED_MARKETPLACE
      && row.mediaType === UniversalMediaListSegmentType.MEDIA_DOMAIN;
  };

  const actionsColumn: AppTableColumn = {
    id: 'universalMediaListsActions',
    prop: 'actions',
    displayName: 'Actions',
    type: 'actions',
    actions: [
      {
        id: 'universalMediaListsActionEdit',
        name: 'edit',
        auth: 'canUpdate',
        displayName: 'Edit',
        hrefLink: '/edit',
        hrefIdProp: 'id',
        condition: (row) => hasUniversalMediaListAccess(row, ActivityStatus.ACTIVE_LOWER)
      },
      {
        id: 'universalMediaListsActionView',
        name: 'edit-restore',
        auth: 'canUpdate',
        displayName: 'View',
        hrefLink: '/view',
        hrefIdProp: 'id',
        condition: (row) => {
          if (portalType === 'exchange') {
            return row.status === ActivityStatus.INACTIVE_LOWER;
          }

          return row.listType === UniversalMediaListType.SHARED || row.mediaType !== UniversalMediaListSegmentType.MEDIA_DOMAIN;
        }
      },
      {
        id: 'universalMediaListsActionDuplicate',
        name: 'duplicate',
        auth: 'canCreate',
        displayName: 'Duplicate',
        condition: (row) => hasUniversalMediaListAccess(row, ActivityStatus.ACTIVE_LOWER)
      },
      {
        id: 'universalMediaListsActionArchive',
        name: 'archive',
        auth: 'canDelete',
        displayName: 'Archive',
        desc: `Archiving this media list will immediately affect its associated deals, preventing its use in future deals.
              Ensure this action aligns with your strategy before proceeding.`,
        condition: (row) => hasUniversalMediaListAccess(row, ActivityStatus.ACTIVE_LOWER)
      },
      {
        id: 'universalMediaListsActionUnarchive',
        name: 'restore',
        auth: 'canUpdate',
        displayName: 'Unarchive',
        desc: 'Are you sure you wish to unarchive this universal media list?',
        condition: (row) => hasUniversalMediaListAccess(row, ActivityStatus.INACTIVE_LOWER)
      }
    ]
  };

  const tableColumns: AppTableColumn[] = [
    statusColumn,
    idColumn,
    nameColumn,
    mediaTypeColumn,
    listTypeColumn,
    createdAtColumn,
    updatedAtColumn,
    associatedDealsColumn,
    actionsColumn
  ];


  if (portalType === 'exchange') {
    const mediaCountIndex = tableColumns.length - 1;
    tableColumns.splice(mediaCountIndex, 0, mediaCountColumn);
  }

  return tableColumns;
};
